import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import htmlToPdfmake from 'html-to-pdfmake';
import {
  Content,
  TDocumentDefinitions,
  TFontDictionary,
  Style,
} from 'pdfmake/interfaces';

interface PdfGenerateOptions {
  element: HTMLElement;
  fileName: string;
  customConfig?: Partial<TDocumentDefinitions>;
  excludeElements?: string[];
  printData: PrintData;
}

interface PrintData {
  logo: string;
  orgDetail: {
    address: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
  caseDetail?: {
    student: {
      firstName: string;
      lastName: string;
    };
    supervisorName: string;
  };
}

interface PdfStyles {
  [key: string]: Style;
  header: Style;
  subheader: Style;
  desc: Style;
  address: Style;
  title: Style;
  datalabel: Style;
  dataValue: Style;
  content: Style;
  columns: Style;
}

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  constructor() {
    (pdfMake.vfs as unknown as TFontDictionary) = pdfFonts;
  }

  private readonly BRAND_COLORS = {
    primary: '#17b26a',
    gray600: '#475467',
    gray900: '#101828',
    gray300: '#E4E7EC',
    background: '#F9FAFB',
  };

  private readonly defaultStyles: PdfStyles = {
    header: {
      fontSize: 12,
      bold: true,
      color: this.BRAND_COLORS.gray900,
      margin: [0, 0, 0, 10],
    },
    subheader: {
      fontSize: 12,
      bold: true,
      color: this.BRAND_COLORS.gray900,
      margin: [0, 10, 0, 5],
    },
    desc: {
      fontSize: 10,
      color: this.BRAND_COLORS.gray600,
      lineHeight: 1.4,
      margin: [0, 0, 0, 5],
    },
    address: {
      fontSize: 10,
      color: this.BRAND_COLORS.gray600,
      lineHeight: 1.4,
    },
    title: {
      fontSize: 8,
      bold: true,
      color: this.BRAND_COLORS.gray900,
      margin: [0, 0, 0, 0],
    },
    datalabel: {
      fontSize: 8,
      bold: true,
      color: this.BRAND_COLORS.gray600,
      lineHeight: 1.5,
    },
    dataValue: {
      fontSize: 11,
      color: this.BRAND_COLORS.gray900,
      lineHeight: 1.4,
    },
    content: {
      fontSize: 11,
      color: this.BRAND_COLORS.gray600,
      lineHeight: 1.6,
      margin: [0, 5, 0, 5],
    },
    columns: {
      columnGap: 20,
      margin: [0, 10, 0, 10],
    },
  };

  private defaultConfig(printData: PrintData): Partial<TDocumentDefinitions> {
    return {
      pageSize: 'A4',
      pageMargins: [20, 60, 20, 40],
      defaultStyle: {
        fontSize: 11,
        lineHeight: 1.4,
        color: this.BRAND_COLORS.gray600,
      },
      header: this.getHeaderDefinition(printData),
      footer: (currentPage: number) => ({
        columns: [
          {
            text: new Date().toLocaleDateString(),
            alignment: 'left',
            fontSize: 9,
            color: this.BRAND_COLORS.gray600,
          },
          {
            text: 'Powered by Insights to Behavior®',
            alignment: 'center',
            fontSize: 9,
            bold: true,
            color: this.BRAND_COLORS.gray600,
          },
          {
            text: `Page ${currentPage}`,
            alignment: 'right',
            fontSize: 9,
            color: this.BRAND_COLORS.gray600,
          },
        ],
        margin: [10, 20, 10, 0],
      }),
      styles: this.defaultStyles,
      info: {
        title: 'Student Behavior Report',
        author: 'Insights to Behavior',
        subject: 'Student Assessment Report',
        keywords: 'behavior, student, assessment, report',
      },
    };
  }

  private getHeaderDefinition(printData: PrintData): Content {
    return {
      stack: [
        {
          columns: [
            {
              image: printData.logo,
              width: 125,
              fit: [125, 40], // Fit the image within 125x40 dimensions (reduces height)
              alignment: 'left',
            },
            {
              stack: [
                `${printData?.orgDetail?.address || ''}, ${
                  printData?.orgDetail?.city || ''
                }`,
                `${printData?.orgDetail?.state || ''}, ${
                  printData?.orgDetail?.country || ''
                }, ${printData?.orgDetail?.zipCode || ''}`,
              ],
              fontSize: 10,
              alignment: 'right',
            },
          ],
          margin: [20, 10, 20, 0],
        },
        this.getHorizontalLine(),
      ],
    } as Content;
  }

  private getHorizontalLine() {
    return {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 0.5,
          lineColor: this.BRAND_COLORS.gray300,
        },
      ],
      margin: [0, 5, 0, 8],
    };
  }
  async generatePDF(options: PdfGenerateOptions): Promise<void> {
    const {
      element,
      fileName,
      customConfig = {},
      excludeElements = [],
      printData,
    } = options;

    if (!element) {
      throw new Error('Element not found');
    }

    const tempElement = element.cloneNode(true) as HTMLElement;
    for (const selector of excludeElements) {
      const elementsToRemove = tempElement.querySelectorAll(selector);
      for (const el of elementsToRemove) {
        el.remove();
      }
    }

    const pdfMakeContent = htmlToPdfmake(tempElement.innerHTML);

    const documentDefinition: TDocumentDefinitions = {
      ...this.defaultConfig(printData),
      ...customConfig,
      content: pdfMakeContent,
    };

    const pdf = pdfMake.createPdf(documentDefinition);
    pdf.download(fileName);
  }
}
